import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { User, AuthContextType } from '../types';
import ApiService from '../services/api';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(localStorage.getItem('authToken'));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const initializeAuth = async () => {
      const storedToken = localStorage.getItem('authToken');
      if (storedToken) {
        try {
          setLoading(true);
          const response = await ApiService.auth.profile();
          setUser(response.data);
          setToken(storedToken);
        } catch (error: any) {
          console.error('Error loading profile:', error);
          // Only clear auth if it's a 401 (unauthorized) error
          if (error.response?.status === 401) {
            localStorage.removeItem('authToken');
            localStorage.removeItem('refreshToken');
            setToken(null);
            setUser(null);
          } else {
            // Keep the token for other errors (network issues, etc.)
            // Try to decode user from token if available
            setToken(storedToken);
            try {
              const tokenPayload = JSON.parse(atob(storedToken.split('.')[1]));
              setUser(tokenPayload.data || null);
            } catch (decodeError) {
              console.error('Error decoding token:', decodeError);
            }
          }
        }
      }
      setLoading(false);
    };

    initializeAuth();
  }, []);

  const login = async (username: string, password: string): Promise<void> => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await ApiService.auth.login(username, password);
      const { token: authToken, user: userData } = response.data;
      
      localStorage.setItem('authToken', authToken);
      setToken(authToken);
      setUser(userData);
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Error al iniciar sesión';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const register = async (userData: any): Promise<void> => {
    try {
      setLoading(true);
      setError(null);
      
      await ApiService.auth.register(userData);
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Error en el registro';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const logout = async (): Promise<void> => {
    try {
      await ApiService.auth.logout();
    } catch (error) {
      console.error('Error al cerrar sesión:', error);
    } finally {
      localStorage.removeItem('authToken');
      localStorage.removeItem('refreshToken');
      setToken(null);
      setUser(null);
    }
  };

  const updateProfile = async (profileData: any): Promise<void> => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await ApiService.auth.profile();
      setUser(response.data);
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Error al actualizar perfil';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const changePassword = async (
    currentPassword: string, 
    newPassword: string
  ): Promise<void> => {
    try {
      setLoading(true);
      setError(null);
      
      await ApiService.auth.changePassword(currentPassword, newPassword, newPassword);
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Error al cambiar contraseña';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const value: AuthContextType = {
    user,
    token,
    login,
    register,
    logout,
    updateProfile,
    changePassword,
    loading,
    error,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};